// 업무지원 — 일정관리 (개인·조직 일정, 상세/수정/취소, 조직별 하위 포함, 지정 인원, 뷰 필터, 취소 일정 표시, 웹 2열 캘린더|일정, react-big-calendar 월·주·일·목록 + 드래그 이동·리사이즈)
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { addDays, endOfDay, endOfMonth, endOfWeek, startOfDay, startOfMonth, startOfWeek } from 'date-fns'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'
import WorkCalendarRbc from '../components/WorkCalendarRbc'
import CalendarEventEditor from '../components/CalendarEventEditor'
import MenuGuideButton from '../components/MenuGuideButton'
import CalendarFiltersPanel from '../components/CalendarFiltersPanel'
import { PageFrame, PageHeader } from '../ui/PageLayout'

/** 백엔드 CalendarOrgAdminRoles + 운영자(팀 일정 조회) */
const TEAM_CALENDAR_VIEW_ROLES = [
    Roles.OPERATOR,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
    Roles.GA_LEADER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_OTHER_ADMIN,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_MANAGER,
    Roles.INSURER_MASTER_ADMIN,
    Roles.INSURER_MASTER_DEPUTY_ADMIN,
    Roles.INSURER_LEADER,
    Roles.INSURER_DEPUTY_LEADER,
    Roles.INSURER_OTHER_ADMIN,
    Roles.INSURER_BRANCH_MANAGER,
    Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    Roles.INSURER_MANAGER,
]

const PERSONAL_CATEGORY_OPTIONS = [
    { value: '', label: '선택 안 함' },
    { value: 'MEETING', label: '회의' },
    { value: 'CLIENT_MEETING', label: '미팅' },
    { value: 'TRIP', label: '출장' },
    { value: 'LEAVE', label: '휴가' },
    { value: 'TRAINING', label: '교육' },
    { value: 'OTHER', label: '기타' },
]

const ORG_EVENT_CREATE_ROLES = [
    Roles.OPERATOR,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
    Roles.GA_LEADER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_OTHER_ADMIN,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_MANAGER,
    Roles.INSURER_MASTER_ADMIN,
    Roles.INSURER_MASTER_DEPUTY_ADMIN,
    Roles.INSURER_LEADER,
    Roles.INSURER_DEPUTY_LEADER,
    Roles.INSURER_OTHER_ADMIN,
    Roles.INSURER_BRANCH_MANAGER,
    Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    Roles.INSURER_MANAGER,
    Roles.INSURER_EDUCATION_MANAGER,
    Roles.INSURER_PLAN_MANAGER,
]

const LS_HIDDEN_HOST_ORGS = 'sgez.calendar.hiddenHostOrgIds'
const LS_SHOW_CUSTOMER_CONTACT_EVENTS = 'sgez.calendar.showCustomerContactEvents'
const LS_SHOW_MATURITY_EVENTS = 'sgez.calendar.showMaturityEvents'
const LS_SHOW_CLAIM_EVENTS = 'sgez.calendar.showClaimEvents'

function unwrap(res) {
    const d = res?.data
    if (d && d.data !== undefined) return d.data
    return d
}

function isCustomerContactEvent(ev) {
    return String(ev?.title || '').trim().startsWith('[연락]')
}

function isMaturityEvent(ev) {
    return String(ev?.title || '').trim().startsWith('[만기고객안내]')
}

function isClaimEvent(ev) {
    return ev?.calendarSourceKind === 'CLAIM_DAILY'
}

function monthRange(year, monthIndex) {
    const from = new Date(year, monthIndex, 1, 0, 0, 0)
    const to = new Date(year, monthIndex + 1, 0, 23, 59, 59)
    return { from: from.toISOString(), to: to.toISOString() }
}

/** react-big-calendar Agenda 기본 length(일) — API 조회 구간과 반드시 맞출 것 */
const RBC_AGENDA_LENGTH_DAYS = 30

/** 캘린더 뷰에 맞춰 API 조회 구간(from/to ISO) */
function visibleRangeForView(date, view) {
    const d = date instanceof Date ? date : new Date(date)
    if (!Number.isFinite(d.getTime())) {
        const n = new Date()
        return monthRange(n.getFullYear(), n.getMonth())
    }
    if (view === 'agenda') {
        // Agenda는 `date`부터 length일(기본 30) 구간만 표시한다. 월 단위만 불러오면
        // 월 초·중반 일정이 `date` 이전에만 있을 때 목록이 비는 문제가 생긴다.
        const from = startOfDay(d)
        const to = endOfDay(addDays(from, RBC_AGENDA_LENGTH_DAYS))
        return { from: from.toISOString(), to: to.toISOString() }
    }
    if (view === 'month') {
        const sm = startOfMonth(d)
        const em = endOfMonth(d)
        const from = startOfWeek(sm, { weekStartsOn: 0 })
        const to = endOfWeek(em, { weekStartsOn: 0 })
        return { from: from.toISOString(), to: to.toISOString() }
    }
    if (view === 'week') {
        const from = startOfWeek(d, { weekStartsOn: 0 })
        const to = endOfWeek(d, { weekStartsOn: 0 })
        return { from: from.toISOString(), to: to.toISOString() }
    }
    const from = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
    const to = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)
    return { from: from.toISOString(), to: to.toISOString() }
}

function occurrenceOriginalFromResource(ev) {
    if (!ev?.occurrenceKey || typeof ev.occurrenceKey !== 'string') return null
    const k = ev.occurrenceKey
    const pipe = k.indexOf('|')
    if (pipe < 0) return null
    return k.slice(pipe + 1)
}

function buildPatchPayload(detail, start, end, sourceEvent) {
    const useDef = detail.useDefaultReminders !== false
    let reminderOffsetsMinutes = null
    if (!useDef) {
        let arr = []
        try {
            if (detail.reminderOffsetsJson) arr = JSON.parse(detail.reminderOffsetsJson)
        } catch {
            arr = []
        }
        reminderOffsetsMinutes = Array.isArray(arr) && arr.length ? arr.map(Number) : []
    }
    const base = {
        title: detail.title,
        description: detail.description ?? null,
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        allDay: !!detail.allDay,
        timeZone: detail.timeZone || 'Asia/Seoul',
        useDefaultReminders: useDef,
        reminderOffsetsMinutes,
    }
    // BE: occurrenceOriginalStart 는 반복(RRULE) 일정에만 허용. 목록 API는 비반복에도 occurrenceKey 를 붙이므로 조건부로만 전송.
    const orig = occurrenceOriginalFromResource(sourceEvent)
    const rruleForOcc = (detail?.rrule && String(detail.rrule).trim()) || ''
    if (orig && rruleForOcc) {
        base.occurrenceOriginalStart = orig
    }
    if (detail.eventKind === 'ORG') {
        const orgTargets = Array.isArray(detail.orgTargets)
            ? detail.orgTargets.map((t) => ({
                orgId: Number(t.orgId),
                includeDescendants: !!t.includeDescendants,
            }))
            : []
        const explicitUserIds = Array.isArray(detail.explicitUserIds) ? detail.explicitUserIds.map(Number) : []
        return { ...base, orgTargets, explicitUserIds }
    }
    const share = {
        personalCategory: detail.personalCategory != null && String(detail.personalCategory).trim()
            ? String(detail.personalCategory).trim()
            : '',
        hideDetailsForOrgAdmins: !!detail.hideDetailsForOrgAdmins,
        busyShareOrgId: detail.busyShareOrgId != null ? Number(detail.busyShareOrgId) : null,
    }
    return { ...base, orgTargets: null, explicitUserIds: null, ...share }
}

export default function CalendarPage() {
    const { user, hasAnyRole, refreshUser } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()
    const location = useLocation()
    const navigate = useNavigate()
    const [calDate, setCalDate] = useState(() => new Date())
    const [calView, setCalView] = useState('month')
    const viewRange = useMemo(() => visibleRangeForView(calDate, calView), [calDate, calView])
    const [includeCancelled, setIncludeCancelled] = useState(false)
    const [showCustomerContactEvents, setShowCustomerContactEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_CUSTOMER_CONTACT_EVENTS) !== 'false'
        } catch {
            return true
        }
    })
    const [showMaturityEvents, setShowMaturityEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_MATURITY_EVENTS) !== 'false'
        } catch {
            return true
        }
    })
    const [showClaimEvents, setShowClaimEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_CLAIM_EVENTS) !== 'false'
        } catch {
            return true
        }
    })
    const [groupCustomerAutoEvents, setGroupCustomerAutoEvents] = useState(false)
    const [hiddenHostOrgIds, setHiddenHostOrgIds] = useState(() => new Set())
    const [teamVisibleOrgIds, setTeamVisibleOrgIds] = useState(() => new Set())
    const [filtersExpanded, setFiltersExpanded] = useState(true)

    const currentUserId = user?.id != null ? Number(user.id) : null
    const canCreateOrg = hasAnyRole(ORG_EVENT_CREATE_ROLES) && user?.orgType !== 'PERSONAL'
    const canUseTeamCalendarView = hasAnyRole(TEAM_CALENDAR_VIEW_ROLES) && user?.orgType !== 'PERSONAL'

    const uiPrefsQuery = useQuery({
        queryKey: ['calendar', 'ui-preferences', currentUserId],
        queryFn: async () => {
            const res = await api.get('/calendar/ui-preferences')
            const data = unwrap(res)
            let ids = Array.isArray(data?.hiddenHostOrgIds) ? data.hiddenHostOrgIds.map(Number).filter(Boolean) : []
            let teamIds = Array.isArray(data?.teamCalendarVisibleOrgIds)
                ? data.teamCalendarVisibleOrgIds.map(Number).filter(Boolean)
                : []
            const eventTypePrefs = {
                showCustomerContactEvents: data?.showCustomerContactEvents !== false,
                showMaturityEvents: data?.showMaturityEvents !== false,
                showClaimEvents: data?.showClaimEvents !== false,
                groupCustomerAutoEvents: data?.groupCustomerAutoEvents === true,
            }
            const legacyKeysToRemove = []
            let shouldPersistUiPrefs = false
            if (ids.length === 0) {
                try {
                    const raw = localStorage.getItem(LS_HIDDEN_HOST_ORGS)
                    if (raw) {
                        const parsed = JSON.parse(raw)
                        if (Array.isArray(parsed) && parsed.length) {
                            ids = parsed.map(Number).filter(Boolean)
                            shouldPersistUiPrefs = true
                            legacyKeysToRemove.push(LS_HIDDEN_HOST_ORGS)
                        }
                    }
                } catch {
                    /* ignore */
                }
            }
            try {
                const legacyEventPrefs = [
                    [LS_SHOW_CUSTOMER_CONTACT_EVENTS, 'showCustomerContactEvents'],
                    [LS_SHOW_MATURITY_EVENTS, 'showMaturityEvents'],
                    [LS_SHOW_CLAIM_EVENTS, 'showClaimEvents'],
                ]
                for (const [storageKey, field] of legacyEventPrefs) {
                    const raw = localStorage.getItem(storageKey)
                    if (raw === 'true' || raw === 'false') {
                        eventTypePrefs[field] = raw !== 'false'
                        shouldPersistUiPrefs = true
                        legacyKeysToRemove.push(storageKey)
                    }
                }
            } catch {
                /* ignore */
            }
            if (shouldPersistUiPrefs) {
                await api.put('/calendar/ui-preferences', {
                    hiddenHostOrgIds: ids,
                    teamCalendarVisibleOrgIds: teamIds,
                    ...eventTypePrefs,
                })
                try {
                    legacyKeysToRemove.forEach((key) => localStorage.removeItem(key))
                } catch {
                    /* ignore */
                }
            }
            return { hiddenHostOrgIds: ids, teamCalendarVisibleOrgIds: teamIds, ...eventTypePrefs }
        },
        enabled: Boolean(currentUserId),
        staleTime: 60_000,
    })
    useEffect(() => {
        setHiddenHostOrgIds(new Set())
        setTeamVisibleOrgIds(new Set())
    }, [currentUserId])
    useEffect(() => {
        if (!uiPrefsQuery.data) return
        setHiddenHostOrgIds(new Set(uiPrefsQuery.data.hiddenHostOrgIds || []))
        setTeamVisibleOrgIds(new Set(uiPrefsQuery.data.teamCalendarVisibleOrgIds || []))
        setShowCustomerContactEvents(uiPrefsQuery.data.showCustomerContactEvents !== false)
        setShowMaturityEvents(uiPrefsQuery.data.showMaturityEvents !== false)
        setShowClaimEvents(uiPrefsQuery.data.showClaimEvents !== false)
        setGroupCustomerAutoEvents(uiPrefsQuery.data.groupCustomerAutoEvents === true)
    }, [uiPrefsQuery.data])

    const saveUiPrefsMutation = useMutation({
        mutationFn: async ({
            hiddenHostOrgIds: hidden,
            teamCalendarVisibleOrgIds: team,
            showCustomerContactEvents: showContact,
            showMaturityEvents: showMaturity,
            showClaimEvents: showClaim,
            groupCustomerAutoEvents: groupCustomerAuto,
        }) => {
            await api.put('/calendar/ui-preferences', {
                hiddenHostOrgIds: hidden,
                teamCalendarVisibleOrgIds: team,
                ...(typeof showContact === 'boolean' ? { showCustomerContactEvents: showContact } : {}),
                ...(typeof showMaturity === 'boolean' ? { showMaturityEvents: showMaturity } : {}),
                ...(typeof showClaim === 'boolean' ? { showClaimEvents: showClaim } : {}),
                ...(typeof groupCustomerAuto === 'boolean' ? { groupCustomerAutoEvents: groupCustomerAuto } : {}),
            })
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['calendar', 'ui-preferences'] })
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
        },
    })

    const digestPrefsMutation = useMutation({
        /** 한 번에 바꾸는 필드만 보냄 — 나머지는 서버에서 유지(독립 토글). */
        mutationFn: async (partial) => {
            await api.put('/users/me', partial)
        },
        onSuccess: async () => {
            show('표시 설정을 저장했습니다. 이후 캘린더와 요약 일정에 반영됩니다.', 'success')
            if (typeof refreshUser === 'function') {
                await refreshUser()
            }
            await qc.invalidateQueries({ queryKey: ['calendar-events'] })
            await qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '저장에 실패했습니다.'
            show(msg, 'error')
        },
    })

    const showInsDigest = user?.calendarShowInsuranceAgeDigest !== false
    const showBirthDigest = user?.calendarShowBirthdayDigest !== false
    const showAnnivDigest = user?.calendarShowAnniversaryDigest !== false


    const eventsQuery = useQuery({
        queryKey: ['calendar-events', currentUserId, viewRange.from, viewRange.to, includeCancelled, showInsDigest, showBirthDigest, showAnnivDigest],
        queryFn: async () => {
            const res = await api.get('/calendar/events', {
                params: { from: viewRange.from, to: viewRange.to, includeCancelled },
            })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
        enabled: Boolean(currentUserId),
    })

    const teamOrgIdsParam = useMemo(
        () => [...teamVisibleOrgIds].filter((n) => n > 0).sort((a, b) => a - b),
        [teamVisibleOrgIds],
    )

    const teamEventsQuery = useQuery({
        queryKey: ['calendar-team-events', currentUserId, viewRange.from, viewRange.to, teamOrgIdsParam.join(',')],
        queryFn: async () => {
            const res = await api.get('/calendar/team-members/events', {
                params: { from: viewRange.from, to: viewRange.to, orgIds: teamOrgIdsParam },
            })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
        enabled: Boolean(currentUserId) && canUseTeamCalendarView && teamOrgIdsParam.length > 0,
        staleTime: 30_000,
    })

    const [selectedId, setSelectedId] = useState(null)
    const [detailOpen, setDetailOpen] = useState(false)
    const [autoGroupDetail, setAutoGroupDetail] = useState(null)

    const detailQuery = useQuery({
        queryKey: ['calendar-event', currentUserId, selectedId],
        queryFn: async () => {
            const res = await api.get(`/calendar/events/${selectedId}`)
            return unwrap(res)
        },
        enabled: Boolean(currentUserId && selectedId && detailOpen),
    })

    const autoGroupDetailQuery = useQuery({
        queryKey: [
            'calendar-auto-event-group',
            currentUserId,
            autoGroupDetail?.kind,
            autoGroupDetail?.date,
            autoGroupDetail?.page || 0,
        ],
        queryFn: async () => {
            const res = await api.get('/calendar/auto-event-groups', {
                params: {
                    kind: autoGroupDetail.kind,
                    date: autoGroupDetail.date,
                    page: autoGroupDetail.page || 0,
                    size: 50,
                },
            })
            return unwrap(res)
        },
        enabled: Boolean(currentUserId && autoGroupDetail?.kind && autoGroupDetail?.date),
    })
    const orgOptionsQuery = useQuery({
        queryKey: ['orgs-options', 'mine', currentUserId],
        queryFn: async () => {
            const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
        enabled: Boolean(currentUserId),
    })

    const editorRef = useRef(null)

    const patchScheduleMut = useMutation({
        mutationFn: async ({ id, start, end, sourceEvent }) => {
            const res = await api.get(`/calendar/events/${id}`)
            const detail = unwrap(res)
            const payload = buildPatchPayload(detail, start, end, sourceEvent)
            const patchRes = await api.patch(`/calendar/events/${id}`, payload)
            return unwrap(patchRes)
        },
        onSuccess: () => {
            show('일정 시간을 변경했습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-event'] })
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '시간 변경에 실패했습니다'
            show(msg, 'error')
        },
    })

    const cancelMut = useMutation({
        mutationFn: async (id) => {
            const res = await api.post(`/calendar/events/${id}/cancel`)
            return unwrap(res)
        },
        onSuccess: () => {
            show('일정을 취소했습니다. 연결된 고객 상세와 캘린더에서도 더 이상 진행 일정으로 표시되지 않습니다.', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-event'] })
            setDetailOpen(false)
            setSelectedId(null)
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '취소에 실패했습니다'
            show(msg, 'error')
        },
    })

    function openPersonalAt(startDate, endDate) {
        editorRef.current?.openPersonalAt(startDate, endDate)
    }

    function handleSelectCalendarSlot(slotInfo) {
        if (!slotInfo?.start) return
        openPersonalAt(slotInfo.start, slotInfo.end)
    }

    const clearRouterState = useCallback(() => {
        navigate(
            { pathname: location.pathname, search: location.search, hash: location.hash },
            { replace: true, state: {} },
        )
    }, [location.hash, location.pathname, location.search, navigate])

    useEffect(() => {
        const st = location.state
        if (!st || typeof st !== 'object') return
        if (st.openAutoEventGroup?.kind && st.openAutoEventGroup?.date) {
            setSelectedId(null)
            setDetailOpen(false)
            setAutoGroupDetail({
                kind: String(st.openAutoEventGroup.kind),
                date: String(st.openAutoEventGroup.date),
                title: String(st.openAutoEventGroup.title || ''),
                page: 0,
            })
            clearRouterState()
            return
        }
        if (st.openEventId != null) {
            const id = Number(st.openEventId)
            if (id) {
                setSelectedId(id)
                setDetailOpen(true)
            }
            clearRouterState()
            return
        }
        if (st.draftSlot?.start) {
            openPersonalAt(new Date(st.draftSlot.start), st.draftSlot.end ? new Date(st.draftSlot.end) : null)
            clearRouterState()
        }
        // openPersonalAt는 모달 초기화까지 한 번에 처리 — 라우터 state 소비용
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, clearRouterState])

    function openPersonal() {
        const d = new Date()
        d.setMinutes(0, 0, 0)
        const e = new Date(d.getTime() + 60 * 60 * 1000)
        openPersonalAt(d, e)
    }

    function openOrg() {
        editorRef.current?.openOrg()
    }

    const onCommitCalendarRange = useCallback(
        async ({ event, start, end }) => {
            if (!event?.id || event.calendarSourceKind === 'CUSTOMER_AUTO_GROUP') return
            if (!end || !(end > start)) {
                show('종료 시각이 시작보다 빨라 변경할 수 없습니다', 'error')
                return
            }
            await patchScheduleMut.mutateAsync({ id: event.id, start, end, sourceEvent: event })
        },
        [patchScheduleMut, show],
    )

    function openDetail(ev) {
        if (ev?.calendarSourceKind === 'CUSTOMER_AUTO_GROUP') {
            setSelectedId(null)
            setDetailOpen(false)
            setAutoGroupDetail({
                kind: String(ev.autoDigestKind || ''),
                date: String(ev.autoEventDate || ''),
                title: String(ev.title || ''),
                page: 0,
            })
            return
        }
        if (ev?.calendarSourceKind === 'CLAIM_DAILY') {
            navigate('/support/claims/applications')
            return
        }
        setSelectedId(ev.id)
        setDetailOpen(true)
    }

    function canEditEvent(ev) {
        if (!user || !ev || ev.cancelled) return false
        if (ev.calendarSourceKind === 'CLAIM_DAILY' || ev.calendarSourceKind === 'CUSTOMER_AUTO_GROUP') return false
        const uid = Number(user.id)
        if (Number(ev.createdByUserId) === uid) return true
        if (ev.eventKind === 'PERSONAL' && Number(ev.ownerUserId) === uid) return true
        return false
    }

    function startEditFromDetail() {
        const d = detailQuery.data
        if (!d) return
        setDetailOpen(false)
        editorRef.current?.startEditFromDetail(d)
    }

    const rawEventsBase = eventsQuery.data || []
    const teamEventsRaw = teamEventsQuery.data || []
    const rawEvents = useMemo(() => {
        const keys = new Set(
            rawEventsBase.map((e) => (e.occurrenceKey ? String(e.occurrenceKey) : `${e.id}|${e.startAt}`)),
        )
        const extra = teamEventsRaw.filter(
            (e) => !keys.has(e.occurrenceKey ? String(e.occurrenceKey) : `${e.id}|${e.startAt}`),
        )
        return [...rawEventsBase, ...extra]
    }, [rawEventsBase, teamEventsRaw])
    const hostOrgOptions = useMemo(() => {
        const m = new Map()
        for (const ev of rawEvents) {
            if (ev.hostOrgId != null && ev.hostOrgName) {
                m.set(Number(ev.hostOrgId), String(ev.hostOrgName))
            }
        }
        return [...m.entries()].map(([id, name]) => ({ id, name }))
    }, [rawEvents])

    const visibleEvents = useMemo(() => {
        return rawEvents.filter((ev) => {
            if (!currentUserId) return false
            if (ev.calendarLayer === 'TEAM_MEMBER_PERSONAL' && !canUseTeamCalendarView) return false
            if (ev.eventKind === 'PERSONAL' && ev.calendarLayer !== 'TEAM_MEMBER_PERSONAL') {
                const ownerId = ev.ownerUserId != null ? Number(ev.ownerUserId) : null
                if (ownerId && ownerId !== currentUserId) return false
            }
            if (!showCustomerContactEvents && isCustomerContactEvent(ev)) return false
            if (!showMaturityEvents && isMaturityEvent(ev)) return false
            if (!showClaimEvents && isClaimEvent(ev)) return false
            if (ev.hostOrgId == null) return true
            return !hiddenHostOrgIds.has(Number(ev.hostOrgId))
        })
    }, [rawEvents, currentUserId, canUseTeamCalendarView, hiddenHostOrgIds, showCustomerContactEvents, showMaturityEvents, showClaimEvents])

    function toggleHiddenHost(orgId) {
        setHiddenHostOrgIds((prev) => {
            const next = new Set(prev)
            if (next.has(orgId)) next.delete(orgId)
            else next.add(orgId)
            saveUiPrefsMutation.mutate({
                hiddenHostOrgIds: [...next],
                teamCalendarVisibleOrgIds: [...teamVisibleOrgIds],
            })
            return next
        })
    }

    const teamCalendarOrgChoices = useMemo(() => {
        const rows = orgOptionsQuery.data || []
        if (user?.roles?.includes?.(Roles.OPERATOR)) return rows
        const oid = Number(user?.orgId)
        if (!oid) return []
        return rows.filter((o) => Number(o.id) === oid)
    }, [orgOptionsQuery.data, user?.orgId, user?.roles])

    const teamCalendarOrgChoicesResolved = useMemo(() => {
        if (teamCalendarOrgChoices.length) return teamCalendarOrgChoices
        const oid = Number(user?.orgId)
        if (!oid || !canUseTeamCalendarView) return []
        return [{ id: oid, name: user?.orgName || `조직 #${oid}` }]
    }, [teamCalendarOrgChoices, user?.orgId, user?.orgName, canUseTeamCalendarView])

    function toggleTeamVisibleOrg(orgId) {
        setTeamVisibleOrgIds((prev) => {
            const next = new Set(prev)
            if (next.has(orgId)) next.delete(orgId)
            else next.add(orgId)
            saveUiPrefsMutation.mutate({
                hiddenHostOrgIds: [...hiddenHostOrgIds],
                teamCalendarVisibleOrgIds: [...next],
            })
            return next
        })
    }

    function saveEventTypeVisibility(next) {
        saveUiPrefsMutation.mutate({
            hiddenHostOrgIds: [...hiddenHostOrgIds],
            teamCalendarVisibleOrgIds: [...teamVisibleOrgIds],
            showCustomerContactEvents,
            showMaturityEvents,
            showClaimEvents,
            groupCustomerAutoEvents,
            ...next,
        })
    }
    if (eventsQuery.isLoading) return <Loading />

    return (
        <PageFrame>
            <PageHeader
                title="일정관리"
                titleAccessory={<MenuGuideButton slug="calendar" title="일정관리 가이드" />}
                actions={(
                    <div className="flex flex-wrap gap-2">
                    <Link
                        to="/work/personal-tasks"
                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        내 업무 바로가기
                    </Link>
                    <button type="button" className="rounded bg-sky-600 text-white px-3 py-1.5 text-sm" onClick={openPersonal}>
                        개인 일정 추가
                    </button>
                    {canCreateOrg && (
                        <button type="button" className="rounded border border-sky-600 text-sky-700 px-3 py-1.5 text-sm" onClick={openOrg}>
                            조직 일정 추가
                        </button>
                    )}
                    </div>
                )}
            />

            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>
                    {calDate.getFullYear()}년 {calDate.getMonth() + 1}월 기준 · 표시 {visibleEvents.length}건 / 내 일정 구간 {rawEventsBase.length}건
                    {canUseTeamCalendarView && teamOrgIdsParam.length > 0 && (
                        <span className="text-slate-500"> · 팀(공유) {teamEventsRaw.length}건</span>
                    )}
                </span>
                <span className="text-xs text-slate-400 hidden sm:inline">월·주·일·목록 전환 · 드래그는 수정 가능한 일정만</span>
            </div>

            <CalendarFiltersPanel
                panelIdPrefix="calendar-page"
                filtersExpanded={filtersExpanded}
                onFiltersExpandedChange={setFiltersExpanded}
                showInsDigest={showInsDigest}
                showBirthDigest={showBirthDigest}
                showAnnivDigest={showAnnivDigest}
                groupCustomerAutoEvents={groupCustomerAutoEvents}
                onGroupCustomerAutoEventsChange={(value) => {
                    setGroupCustomerAutoEvents(value)
                    saveEventTypeVisibility({ groupCustomerAutoEvents: value })
                }}
                digestPrefsMutation={digestPrefsMutation}
                includeCancelled={includeCancelled}
                onIncludeCancelledChange={setIncludeCancelled}
                showCustomerContactEvents={showCustomerContactEvents}
                onShowCustomerContactEventsChange={(value) => {
                    setShowCustomerContactEvents(value)
                    saveEventTypeVisibility({ showCustomerContactEvents: value })
                }}
                showMaturityEvents={showMaturityEvents}
                onShowMaturityEventsChange={(value) => {
                    setShowMaturityEvents(value)
                    saveEventTypeVisibility({ showMaturityEvents: value })
                }}
                showClaimEvents={showClaimEvents}
                onShowClaimEventsChange={(value) => {
                    setShowClaimEvents(value)
                    saveEventTypeVisibility({ showClaimEvents: value })
                }}
                hostOrgOptions={hostOrgOptions}
                hiddenHostOrgIds={hiddenHostOrgIds}
                onToggleHiddenHost={toggleHiddenHost}
                canUseTeamCalendarView={canUseTeamCalendarView}
                teamCalendarOrgChoicesResolved={teamCalendarOrgChoicesResolved}
                teamVisibleOrgIds={teamVisibleOrgIds}
                onToggleTeamVisibleOrg={toggleTeamVisibleOrg}
            />

            <div className="space-y-2">
                <div className="hidden lg:grid lg:grid-cols-[7fr_3fr] lg:gap-4">
                    <h2 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-1.5">캘린더</h2>
                    <h2 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-1.5">일정</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[7fr_3fr] lg:items-stretch">
                    <div className="min-w-0">
                        <WorkCalendarRbc
                            events={visibleEvents}
                            view={calView}
                            date={calDate}
                            onNavigate={setCalDate}
                            onView={setCalView}
                            onSelectEvent={openDetail}
                            canEditEvent={canEditEvent}
                            onCommitRange={onCommitCalendarRange}
                            onSelectSlot={handleSelectCalendarSlot}
                        />
                    </div>
                    <aside className="min-w-0 flex flex-col gap-2">
                        <h2 className="text-sm font-semibold text-slate-800 border-b border-slate-200 pb-1.5 lg:hidden">일정</h2>
                        <div className="rounded border divide-y bg-white overflow-y-auto min-h-[200px] max-h-[55vh] lg:max-h-none lg:h-[min(720px,calc(100vh-240px))]">
                            {visibleEvents.length === 0 && (
                                <div className="p-4 text-sm text-slate-500">이 구간에 표시할 일정이 없습니다.</div>
                            )}
                            {visibleEvents.map((ev) => (
                                <button
                                    key={ev.occurrenceKey || String(ev.id)}
                                    type="button"
                                    className="w-full text-left p-2.5 sm:p-3 hover:bg-slate-50 flex flex-wrap gap-2 justify-between"
                                    onClick={() => openDetail(ev)}
                                >
                                    <div className="min-w-0">
                                        <div className="font-medium text-slate-900 text-xs truncate leading-snug">
                                            {ev.title}
                                            {ev.cancelled && <span className="ml-1 text-[10px] text-rose-600">(취소)</span>}
                                        </div>
                                        <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">
                                            {ev.calendarLayer === 'TEAM_MEMBER_PERSONAL' && ev.ownerName
                                                ? `팀 · ${ev.ownerName}`
                                                : ev.calendarSourceKind === 'CLAIM_DAILY'
                                                    ? `청구 ${ev.claimCount || 0}건`
                                                    : ev.eventKind === 'ORG'
                                                    ? '조직'
                                                    : '개인'}
                                            {' · '}
                                            {formatMenuInstant(ev, 'startAt')} ~ {formatMenuInstant(ev, 'endAt')}
                                        </div>
                                        {ev.hostOrgName && (
                                            <div className="text-[10px] text-slate-500 truncate leading-snug">기준: {ev.hostOrgName}</div>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-sky-600 self-center shrink-0">상세</div>
                                </button>
                            ))}
                        </div>
                    </aside>
                </div>
            </div>

            {autoGroupDetail && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
                    <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-4 shadow-lg space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h2 className="text-base font-semibold text-slate-900">{autoGroupDetail.title || '고객 일정'}</h2>
                                <p className="mt-0.5 text-xs text-slate-500">{autoGroupDetail.date}</p>
                            </div>
                            <button
                                type="button"
                                className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                onClick={() => setAutoGroupDetail(null)}
                            >
                                닫기
                            </button>
                        </div>
                        {autoGroupDetailQuery.isLoading && (
                            <div className="py-6 text-center text-sm text-slate-500">고객 목록을 불러오는 중입니다.</div>
                        )}
                        {autoGroupDetailQuery.isError && (
                            <div className="py-6 text-center text-sm text-rose-600">고객 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</div>
                        )}
                        {!autoGroupDetailQuery.isLoading && !autoGroupDetailQuery.isError && (
                            <>
                                <div className="text-xs text-slate-500">총 {autoGroupDetailQuery.data?.totalElements || 0}명</div>
                                <div className="divide-y rounded border border-slate-200">
                                    {(autoGroupDetailQuery.data?.content || []).map((row) => (
                                        <div key={row.occurrenceKey || String(row.customerRefId)} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-medium text-slate-900">{row.customerName || '이름 미등록'}</div>
                                                {row.customerPhone && <div className="mt-0.5 text-xs text-slate-500">{row.customerPhone}</div>}
                                            </div>
                                            <button
                                                type="button"
                                                className="shrink-0 rounded border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                                onClick={() => {
                                                    setAutoGroupDetail(null)
                                                    navigate(`/support/customers/${row.customerRefId}`)
                                                }}
                                            >
                                                고객 상세
                                            </button>
                                        </div>
                                    ))}
                                    {(autoGroupDetailQuery.data?.content || []).length === 0 && (
                                        <div className="px-3 py-6 text-center text-sm text-slate-500">표시할 고객이 없습니다.</div>
                                    )}
                                </div>
                                {(autoGroupDetailQuery.data?.totalPages || 0) > 1 && (
                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            className="rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={(autoGroupDetail.page || 0) === 0}
                                            onClick={() => setAutoGroupDetail((prev) => ({ ...prev, page: Math.max(0, (prev.page || 0) - 1) }))}
                                        >
                                            이전
                                        </button>
                                        <span className="text-xs text-slate-500">
                                            {(autoGroupDetail.page || 0) + 1} / {autoGroupDetailQuery.data.totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            className="rounded border border-slate-300 px-2.5 py-1.5 text-xs text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            disabled={(autoGroupDetail.page || 0) + 1 >= autoGroupDetailQuery.data.totalPages}
                                            onClick={() => setAutoGroupDetail((prev) => ({ ...prev, page: (prev.page || 0) + 1 }))}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {detailOpen && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
                    <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto">
                        {detailQuery.isLoading && <div className="text-sm text-slate-500">불러오는 중…</div>}
                        {detailQuery.data && (
                            <>
                                <div className="flex justify-between items-start gap-2">
                                    <div className="font-semibold text-lg">{detailQuery.data.title}</div>
                                    <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => setDetailOpen(false)} aria-label="닫기">✕</button>
                                </div>
                                {detailQuery.data.cancelled && <div className="text-sm text-rose-600">취소된 일정입니다.</div>}
                                <div className="text-xs text-slate-500">
                                    {detailQuery.data.eventKind === 'PERSONAL'
                                        && Number(detailQuery.data.ownerUserId) !== Number(user?.id)
                                        ? `팀 뷰 · ${detailQuery.data.ownerName || '구성원'}의 개인 일정`
                                        : detailQuery.data.eventKind === 'ORG'
                                            ? '조직 일정'
                                            : '개인 일정'}
                                    {' · '}
                                    {formatMenuInstant(detailQuery.data, 'startAt')} ~ {formatMenuInstant(detailQuery.data, 'endAt')}
                                </div>
                                {detailQuery.data.eventKind === 'PERSONAL'
                                    && Number(detailQuery.data.ownerUserId) !== Number(user?.id) && (
                                    <p className="text-xs text-indigo-700 bg-indigo-50 rounded px-2 py-1">
                                        다른 사람이 공유한 일정입니다. 수정·취소는 일정 소유자만 할 수 있습니다.
                                    </p>
                                )}
                                {detailQuery.data.personalCategory && (
                                    <div className="text-xs text-slate-600">
                                        카테고리:{' '}
                                        {PERSONAL_CATEGORY_OPTIONS.find((c) => c.value === detailQuery.data.personalCategory)?.label
                                            || detailQuery.data.personalCategory}
                                    </div>
                                )}
                                {detailQuery.data.customerName && (
                                    <div className="flex items-center justify-between gap-2 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm">
                                        <span className="min-w-0 truncate font-medium text-slate-900">
                                            {detailQuery.data.customerName}
                                            {detailQuery.data.customerPhoneMasked ? ` (${detailQuery.data.customerPhoneMasked})` : ''}
                                        </span>
                                        <button
                                            type="button"
                                            className="shrink-0 text-xs font-semibold text-sky-700 hover:text-sky-900"
                                            onClick={() => {
                                                setDetailOpen(false)
                                                navigate(`/support/customers/${detailQuery.data.customerRefId}`)
                                            }}
                                        >
                                            {'\uACE0\uAC1D \uC0C1\uC138'}
                                        </button>
                                    </div>
                                )}
                                {detailQuery.data.eventKind === 'PERSONAL' && detailQuery.data.busyShareOrgName != null && (
                                    <div className="text-xs text-slate-600">공유 조직: {detailQuery.data.busyShareOrgName}</div>
                                )}
                                {detailQuery.data.description && (
                                    <div className="text-sm text-slate-700 whitespace-pre-wrap">{detailQuery.data.description}</div>
                                )}
                                {detailQuery.data.rrule && (
                                    <div className="text-xs text-slate-600 font-mono">반복: {detailQuery.data.rrule}</div>
                                )}
                                {Array.isArray(detailQuery.data.orgTargets) && detailQuery.data.orgTargets.length > 0 && (
                                    <div className="text-sm">
                                        <div className="font-medium text-slate-700">대상 조직</div>
                                        <ul className="mt-1 text-xs text-slate-600 list-disc pl-4">
                                            {detailQuery.data.orgTargets.map((t) => (
                                                <li key={t.orgId}>
                                                    {t.orgName} {t.includeDescendants ? '(하위 포함)' : '(해당 조직만)'}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {Array.isArray(detailQuery.data.explicitUsers) && detailQuery.data.explicitUsers.length > 0 && (
                                    <div className="text-sm">
                                        <div className="font-medium text-slate-700">지정 인원</div>
                                        <ul className="mt-1 text-xs text-slate-600 list-disc pl-4 space-y-0.5">
                                            {detailQuery.data.explicitUsers.map((u) => (
                                                <li key={u.id}>
                                                    {u.name || `사용자 #${u.id}`}
                                                    {u.email ? <span className="text-slate-400"> · {u.email}</span> : null}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {(!Array.isArray(detailQuery.data.explicitUsers) || detailQuery.data.explicitUsers.length === 0) &&
                                    Array.isArray(detailQuery.data.explicitUserIds) &&
                                    detailQuery.data.explicitUserIds.length > 0 && (
                                        <div className="text-xs text-slate-600">지정 인원 userId: {detailQuery.data.explicitUserIds.join(', ')}</div>
                                    )}
                                {!detailQuery.data.cancelled && canEditEvent(detailQuery.data) && (
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        <button type="button" className="rounded border px-3 py-1.5 text-sm" onClick={startEditFromDetail}>
                                            수정
                                        </button>
                                        <button
                                            type="button"
                                            className="rounded border border-rose-300 text-rose-700 px-3 py-1.5 text-sm"
                                            disabled={cancelMut.isPending}
                                            onClick={() => {
                                                if (!window.confirm('이 일정을 취소할까요?\n취소 후에는 고객 상세와 캘린더에서 진행 일정으로 표시되지 않습니다.')) return
                                                cancelMut.mutate(detailQuery.data.id)
                                            }}
                                        >
                                            일정 취소
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            <CalendarEventEditor ref={editorRef} />
        </PageFrame>
    )
}
